import React, { useState, useEffect, useCallback, useRef } from 'react';
import Login from './components/Login';
import Register from './components/Register';
import ScriptExecution from './components/ScriptExecution';
import ScriptResults from './components/ScriptResults';
import FileDownloader from './components/FileDownloader';
import Profile from './components/Profile';
import InvitationManager from './components/InvitationManager';
import NodeManagement from './components/NodeManagement';
import UCloudTokenManager from './components/UCloudTokenManager';
import RewardQuery from './components/RewardQuery';
import BatchImport from './components/BatchImport';
import BatchTaskList from './components/BatchTaskList';
import { API_BASE_URL } from './services/api';
import './App.css';

function App() {
  const [token, setToken] = useState('');
  const [username, setUsername] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [activeTab, setActiveTab] = useState('nodes');
  const [activeAuthTab, setActiveAuthTab] = useState('login');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);

  // 初始化时从localStorage获取认证信息
  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem('token');
      const storedUsername = localStorage.getItem('username');
      const storedIsAdmin = localStorage.getItem('isAdmin') === 'true';
      
      if (storedToken) {
        // 设置初始状态
        setToken(storedToken);
        setUsername(storedUsername || '');
        setIsAdmin(storedIsAdmin);
      }
    };
    
    initAuth();
  }, []);

  // 处理未授权错误的函数
  const handleUnauthorized = useCallback(() => {
    handleLogout();
    // 可以添加一个全局提示，告知用户登录已过期
  }, []);

  // 验证token并获取用户信息
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) return;
      
      // 如果用户名和管理员状态已经存在，则不需要再次验证
      if (username && typeof isAdmin === 'boolean') {
        return;
      }
      
      setIsVerifying(true);
      try {
        const response = await fetch(`${API_BASE_URL}/auth/verify`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (!response.ok) {
          throw new Error('Token验证失败');
        }
        
        const data = await response.json();
        setUsername(data.user.username);
        setIsAdmin(data.user.is_superuser);
        
        // 更新localStorage中的用户信息
        localStorage.setItem('username', data.user.username);
        localStorage.setItem('isAdmin', data.user.is_superuser.toString());
        localStorage.setItem('token', token);
      } catch (err) {
        console.error('Token验证失败:', err);
        handleUnauthorized();
      } finally {
        setIsVerifying(false);
      }
    };
    
    verifyToken();
  }, [token, username, isAdmin, handleUnauthorized]);

  // 处理ESC键关闭菜单
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.keyCode === 27 && isMobileMenuOpen) {
        closeMobileMenu();
      }
    };

    document.addEventListener('keydown', handleEscKey);
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isMobileMenuOpen]);

  // 登录处理
  const handleLogin = (newToken, user) => {
    setToken(newToken);
    setUsername(user.username);
    setIsAdmin(user.is_superuser);
    
    // 持久化存储到localStorage
    localStorage.setItem('token', newToken);
    localStorage.setItem('username', user.username);
    localStorage.setItem('isAdmin', user.is_superuser.toString());
    setActiveTab('nodes');
  };

  // 注册处理
  const handleRegister = (message) => {
    alert(message);
    setActiveAuthTab('login');
  };

  // 登出处理
  const handleLogout = useCallback(() => {
    setToken('');
    setUsername('');
    setIsAdmin(false);
    
    // 从localStorage中删除
    localStorage.removeItem('token');
    localStorage.removeItem('username');
    localStorage.removeItem('isAdmin');
    setActiveTab('nodes');
  }, []);

  // 切换移动端菜单
  const toggleMobileMenu = useCallback(() => {
    setIsMobileMenuOpen(prev => !prev);
  }, []);

  // 关闭移动端菜单
  const closeMobileMenu = useCallback(() => {
    setIsMobileMenuOpen(false);
  }, []);

  // 切换标签页并关闭移动端菜单
  const changeTabAndCloseMenu = useCallback((tab) => {
    setActiveTab(tab);
    closeMobileMenu();
  }, [closeMobileMenu]);

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const setHandleError = (data) => {
    setError(data);
  }

  const setHandleSuccess = (data) => {
    setSuccess(data);
  }

  const clearError = () => {
    setError('');
  }

  const clearSuccess = () => {
    setSuccess('');
  }

  const [modeDiv,setModeDiv] = useState(null);
  const [modeState,setModeState] = useState('');
  const modeStateRef = useRef('');

  useEffect(()=>{
    modeStateRef.current = modeState;
  },[modeState]);

  // 用户已登录，显示主应用
  return (
    <div className="App">
      <header className="app-header">
        <div className="header-content">
          <div className="header-left">
            <h1 className="app-title">Script Processor</h1>
          </div>
          {token && (
            <div className="header-right">
              <div className="user-info">
                <span className="welcome-text">欢迎, {username}!</span>
                <button onClick={handleLogout} className="logout-button">登出</button>
              </div>
              <button 
                className={`mobile-menu-button ${isMobileMenuOpen ? 'active' : ''}`} 
                onClick={toggleMobileMenu}
                aria-label={isMobileMenuOpen ? "关闭菜单" : "打开菜单"}
                aria-expanded={isMobileMenuOpen}
              >
                <span className="menu-icon">{isMobileMenuOpen ? '✕' : '☰'}</span>
              </button>
            </div>
          )}
        </div>
      </header>
      
      {token ? (
        <div className="main-layout">
          <nav 
            className={`sidebar ${isMobileMenuOpen ? 'mobile-open' : ''}`}
            aria-hidden={!isMobileMenuOpen}
          >
            <div className="nav-content">
              <div className="nav-group">
                {isAdmin && (
                  <>
                  <h3 className="nav-group-title">核心功能</h3>
                    <button 
                      className={`nav-item ${activeTab === 'scripts' ? 'active' : ''}`}
                      onClick={() => changeTabAndCloseMenu('scripts')}
                    >
                      <span className="nav-icon">▶</span>
                      脚本执行
                    </button>
                    <button 
                      className={`nav-item ${activeTab === 'results' ? 'active' : ''}`}
                      onClick={() => changeTabAndCloseMenu('results')}
                    >
                      <span className="nav-icon">📊</span>
                      脚本结果
                    </button>
                    <button 
                      className={`nav-item ${activeTab === 'files' ? 'active' : ''}`}
                      onClick={() => changeTabAndCloseMenu('files')}
                    >
                      <span className="nav-icon">📁</span>
                      文件下载
                    </button>
                    <button 
                      className={`nav-item ${activeTab === 'invitations' ? 'active' : ''}`}
                      onClick={() => changeTabAndCloseMenu('invitations')}
                    >
                      <span className="nav-icon">🔑</span>
                      邀请管理
                    </button>
                    <button 
                      className={`nav-item ${activeTab === 'ucloud' ? 'active' : ''}`}
                      onClick={() => changeTabAndCloseMenu('ucloud')}
                    >
                      <span className="nav-icon">🔒</span>
                      密钥管理
                    </button>
                    <button 
                      className={`nav-item ${activeTab === 'batch-import' ? 'active' : ''}`}
                      onClick={() => changeTabAndCloseMenu('batch-import')}
                    >
                      <span className="nav-icon">📋</span>
                      批量导入
                    </button>
                  </>
                )}
              </div>
              
              <div className="nav-group">
                <h3 className="nav-group-title">系统管理</h3>
                <button 
                  className={`nav-item ${activeTab === 'nodes' ? 'active' : ''}`}
                  onClick={() => changeTabAndCloseMenu('nodes')}
                >
                  <span className="nav-icon">⚙️</span>
                  节点管理
                </button>
                <button 
                  className={`nav-item ${activeTab === 'rewards' ? 'active' : ''}`}
                  onClick={() => changeTabAndCloseMenu('rewards')}
                >
                  <span className="nav-icon">💰</span>
                  收益查询
                </button>
                <button 
                  className={`nav-item ${activeTab === 'profile' ? 'active' : ''}`}
                  onClick={() => changeTabAndCloseMenu('profile')}
                >
                  <span className="nav-icon">👤</span>
                  个人资料
                </button>
              </div>
            </div>
            {isMobileMenuOpen && (
              <div 
                className="mobile-menu-overlay" 
                onClick={closeMobileMenu}
                aria-hidden="true"
              ></div>
            )}
          </nav>
          
          <main className="main-content">

            {error && (
              <div className="error-message sticky-message">
                <div className="error-content">
                  <span className="error-icon">⚠️</span>
                  <span className="error-text">{error}</span>
                  <button className="close-button" onClick={clearError}>×</button>
                </div>
              </div>
            )}
            
            {success && (
              <div className="success-message sticky-message">
                <div className="success-content">
                  <span className="success-icon">✓</span>
                  <span className="success-text">{success}</span>
                  <button className="close-button" onClick={clearSuccess}>×</button>
                </div>
              </div>
            )}

            {modeDiv &&(
              <div>{modeDiv}</div>
            )}
            {modeDiv === "batchImport" && (
              <BatchImport
                token={token}
                setSuccess={setSuccess}
                setError={setError}
                onBack={() => setModeDiv("nodeManagement")}
              />
            )}
            {activeTab === 'batch-import' && (
              <BatchImport
                token={token}
                setSuccess={setSuccess}
                setError={setError}
              />
            )}
            {activeTab === 'batch-tasks' && (
              <BatchTaskList
                token={token}
                setSuccess={setSuccess}
                setError={setError}
              />
            )}
            {activeTab === 'invitations' && <InvitationManager token={token} setError={setHandleError} setSuccess={setHandleSuccess} onUnauthorized={handleUnauthorized} />}
            {activeTab === 'scripts' && <ScriptExecution token={token} setError={setHandleError} setSuccess={setHandleSuccess} onUnauthorized={handleUnauthorized} />}
            {activeTab === 'results' && <ScriptResults token={token} setError={setHandleError} setSuccess={setHandleSuccess} onUnauthorized={handleUnauthorized} />}
            {activeTab === 'files' && <FileDownloader token={token} setError={setHandleError} setSuccess={setHandleSuccess} onUnauthorized={handleUnauthorized} />}
            {activeTab === 'nodes' && <NodeManagement token={token} modeStateRef={modeStateRef} setModeState={setModeState} setModeDiv={setModeDiv} setError={setHandleError} setSuccess={setHandleSuccess} isAdmin={isAdmin} onUnauthorized={handleUnauthorized} />}
            {activeTab === 'ucloud' && <UCloudTokenManager token={token} onUnauthorized={handleUnauthorized} />}
            {activeTab === 'profile' && <Profile token={token} setError={setHandleError} setSuccess={setHandleSuccess} onLogout={handleLogout} onUnauthorized={handleUnauthorized} />}
            {activeTab === 'rewards' && <RewardQuery token={token} onUnauthorized={handleUnauthorized} />}
          </main>
        </div>
      ) : (
        <main className="auth-main">
          {activeAuthTab === 'login' ? (
            <Login onLogin={handleLogin} onSwitchToRegister={() => setActiveAuthTab('register')} />
          ) : (
            <Register onRegister={handleRegister} onSwitchToLogin={() => setActiveAuthTab('login')} />
          )}
        </main>
      )}
    </div>
  );
}

export default App;